import { SelectTriggerProps } from '@radix-ui/react-select';
import { useMemo, forwardRef } from 'react';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { MODAL_NAMES } from '@/Domain/store/modal.store';
import { cn } from '@/Domain/utils/cn';

// The backend stores inclusive end times, so 23:59 means "until midnight".
const END_OF_DAY = '23:59';

function generateTimeOptions(includeEndOfDay: boolean, interval = 15) {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += interval) {
      const hour = String(h).padStart(2, '0');
      const minute = String(m).padStart(2, '0');
      times.push(`${hour}:${minute}`);
    }
  }
  if (includeEndOfDay) times.push(END_OF_DAY);
  return times;
}

export type Props = Omit<SelectTriggerProps, 'value' | 'onChange' | 'children'> & {
  value: string; // "HH:MM"
  onChange: (time: string) => void;
  variant?: 'bordered' | 'unbordered';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  includeEndOfDay?: boolean;
};

export const TimeSelect = forwardRef<HTMLButtonElement, Props>(
  (
    {
      value = '00:00',
      onChange,
      variant = 'bordered',
      size = 'md',
      includeEndOfDay = false,
      className,
      ...triggerProps
    },
    ref,
  ) => {
    const containerNode = document.getElementById(MODAL_NAMES.HANDOFF_ACTION_SHEET);

    const timeOptions = useMemo(
      () =>
        generateTimeOptions(includeEndOfDay).map((time) => ({
          value: time,
          label: `${time.slice(0, 2)}:${time.slice(3, 5)}`,
        })),
      [includeEndOfDay],
    );

    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          {...triggerProps}
          ref={ref}
          variant={variant}
          size={size}
          className={cn('w-full', className)}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent containerNode={containerNode}>
          {timeOptions.map(({ value, label }) => (
            <SelectItem value={value} key={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  },
);

TimeSelect.displayName = 'TimeSelect';
